import { useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { Trans } from 'react-i18next'
import styled from 'styled-components'

import { Button, Typography, useHapticFeedback } from '@blade/ui-kit'
import { type CurrencyModel } from '@root/entities/currency'
import { DATE_FORMAT } from '@root/shared'
import { Edit1Icon } from '@root/shared/ui-kit'

import { useCurrencyExpirationLeft } from '../../hooks'
import { premiumFontCss } from '../constants'
import { PremiumCardHeader } from '../premium-card-header'

import { ActivePremiumCardRenew } from './active-premium-card-renew'

import type { PremiumMetaModel } from '../../atoms'
import type { CurrencyWithPremium } from '@root/entities/currency'

type Props = {
  currency: CurrencyModel
  premium: PremiumMetaModel
  onClick?: (currency: CurrencyWithPremium.CurrencyWithPremium) => void
  onEdit: (currency: CurrencyWithPremium.CurrencyWithPremium) => void
  onExtend: (currency: CurrencyWithPremium.CurrencyWithPremium) => void
  onActiveClick: () => void
}

export const ActivePremiumCard: React.FC<Props> = ({
  currency,
  premium: premiumInfo,
  onClick,
  onEdit,
  onExtend,
  onActiveClick,
}) => {
  const haptics = useHapticFeedback()
  const { formatExpirationLeft } = useCurrencyExpirationLeft()

  const handleEdit = () => {
    haptics.impactOccurred('light')
    onEdit({ currency, premium: premiumInfo })
  }

  const [expiresAt] = useAtom(premiumInfo.expiresAt)

  const expirationLeft = formatExpirationLeft(currency, expiresAt)

  return (
    <ActivePremiumCardRoot
      onClick={() => {
        if (onClick) {
          haptics.impactOccurred('light')
          onClick({ currency, premium: premiumInfo })
        }
      }}
    >
      <Header>
        <PremiumCardHeader currency={currency} premium={premiumInfo} />
      </Header>
      <Footer>
        <FooterContent>
          <Rows>
            <TimeLeft>
              <Trans
                i18nKey="premium.purchased.date-due"
                components={[<span key={0} />]}
                values={{
                  date: DateF.format(expiresAt, DATE_FORMAT.dd_MM_yy),
                }}
              />
            </TimeLeft>
            {expirationLeft ? <DaysLeft>{expirationLeft}</DaysLeft> : undefined}
          </Rows>
          <Actions>
            <ActivePremiumCardRenew
              currency={currency}
              premium={premiumInfo}
              onExtend={onExtend}
              onActiveClick={onActiveClick}
            />
            <Button
              onClick={handleEdit}
              iconLeft={<Edit1Icon />}
              variant="soft"
              size="xs"
              radius="rounded"
            />
          </Actions>
        </FooterContent>
      </Footer>
    </ActivePremiumCardRoot>
  )
}

export const ActivePremiumCardRoot = styled.div`
  border-radius: 12px;
  background: var(--background-2);
  display: grid;
`
export const Header = styled.div`
  padding: 10px 14px;
  border-bottom: 1px solid var(--background-3);
`

export const Footer = styled.footer`
  padding: 10px 14px;
  display: grid;
  align-items: center;
  gap: 8px;
`

export const FooterContent = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
`

export const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`
export const Actions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: space-between;
`

export const TimeLeft = styled(Typography).attrs({
  variant: 'body4-semi-bold',
  component: 'div',
})`
  text-align: left;

  span {
    ${premiumFontCss}
  }
`

export const DaysLeft = styled(Typography).attrs({
  variant: 'description-medium',
  component: 'span',
})`
  color: var(--textA-9);
`
